import React, { useState } from 'react';
import { Building, ShieldCheck, Cpu, HardDrive, Database, Settings, Plus, UserPlus, Check, X, Shield, Lock } from 'lucide-react';

interface TenantAdmin {
  id: string;
  adminName: string;
  adminEmail: string;
  schoolName: string;
  slug: string;
  plan: string;
  modules: string[];
  studentCount: number;
}

const INITIAL_TENANT_ADMINS: TenantAdmin[] = [
  { 
    id: '1', 
    adminName: 'Dr. Robert Vance', 
    adminEmail: 'principal@stjude.edu', 
    schoolName: 'St. Jude Special School', 
    slug: 'stjude', 
    plan: 'ENTERPRISE_SCHOOL', 
    studentCount: 142,
    modules: ['aac', 'speech-therapy', 'autism', 'student', 'teacher', 'therapist', 'ai', 'billing'] 
  },
  { 
    id: '2', 
    adminName: 'Dr. Ananya Sharma', 
    adminEmail: 'admin@sunshinetherapy.org', 
    schoolName: 'Sunshine Therapy Centre', 
    slug: 'sunshine', 
    plan: 'PRO_CLINIC', 
    studentCount: 88,
    modules: ['aac', 'speech-therapy', 'therapist', 'billing', 'communication'] 
  },
  { 
    id: '3', 
    adminName: 'Superintendent K. Patil', 
    adminEmail: 'director@districtrehab.gov', 
    schoolName: 'District Rehab Department', 
    slug: 'rehab', 
    plan: 'GOVT_REHAB', 
    studentCount: 350,
    modules: ['aac', 'speech-therapy', 'autism', 'sign-language', 'braille', 'student', 'therapist', 'hospital', 'inventory', 'analytics', 'ai'] 
  }
];

const ALL_16_MODULES = [
  { key: 'aac', label: 'AAC Speech Synthesizer' },
  { key: 'speech-therapy', label: 'Speech & Articulation' },
  { key: 'autism', label: 'Autism & ABA Behavior' },
  { key: 'sign-language', label: 'Sign Language & Gestures' },
  { key: 'braille', label: 'Braille & Tactile Learning' },
  { key: 'student', label: 'Student & IEP Progress' },
  { key: 'therapist', label: 'Therapist Workspace & SOAP' },
  { key: 'teacher', label: 'Teacher & Classroom' },
  { key: 'assessment', label: 'Diagnostic Assessment' },
  { key: 'school-erp', label: 'Special School ERP' },
  { key: 'hospital', label: 'Rehabilitation Hospital' },
  { key: 'billing', label: 'NDIS & Insurance Billing' },
  { key: 'communication', label: 'Parent Communication' },
  { key: 'inventory', label: 'Assistive Device Inventory' },
  { key: 'analytics', label: 'Executive Analytics BI' },
  { key: 'ai', label: 'AI Copilot Summarizer' }
];

export const SuperAdminDashboard: React.FC = () => {
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdmin[]>(INITIAL_TENANT_ADMINS);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);

  // New Admin Form State
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [plan, setPlan] = useState('ENTERPRISE_SCHOOL');
  const [selectedModules, setSelectedModules] = useState<string[]>(['aac', 'student', 'therapist', 'ai']);
  const [successMsg, setSuccessMsg] = useState('');

  const toggleModuleForAdmin = (adminId: string, moduleKey: string) => {
    setTenantAdmins((prev) =>
      prev.map((item) => {
        if (item.id !== adminId) return item;
        const exists = item.modules.includes(moduleKey);
        const updated = exists
          ? item.modules.filter((m) => m !== moduleKey)
          : [...item.modules, moduleKey];
        return { ...item, modules: updated };
      })
    );
  };

  const handleCreateTenantAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminName || !adminEmail || !schoolName) return;

    const slug = schoolName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const newAdmin: TenantAdmin = {
      id: Date.now().toString(),
      adminName,
      adminEmail,
      schoolName,
      slug,
      plan,
      studentCount: 0,
      modules: selectedModules
    };

    setTenantAdmins([newAdmin, ...tenantAdmins]);
    setSuccessMsg(`Tenant Admin "${adminName}" created successfully for ${schoolName}!`);
    setAdminName('');
    setAdminEmail('');
    setSchoolName('');
    setIsAddAdminOpen(false);

    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full uppercase tracking-wider mb-2">
            <Shield className="w-3.5 h-3.5" /> Super Admin Control Plane
          </div>
          <h2 className="text-2xl font-black text-slate-900">Tenant Admin & Feature Authorization Matrix</h2>
          <p className="text-xs text-slate-500">Provision school administrators and toggle module access in real-time</p>
        </div>

        <button
          onClick={() => setIsAddAdminOpen(true)}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 shadow-md transition"
        >
          <UserPlus className="w-4 h-4" />
          <span>Add New Tenant Admin</span>
        </button>
      </div>

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          {successMsg}
        </div>
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><Building className="w-6 h-6" /></div>
          <div>
            <div className="text-xs text-slate-500 font-bold">Total Schools / Clinics</div>
            <div className="text-xl font-black text-slate-900">{tenantAdmins.length} Organizations</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-xl"><UserPlus className="w-6 h-6" /></div>
          <div>
            <div className="text-xs text-slate-500 font-bold">Provisioned Admins</div>
            <div className="text-xl font-black text-slate-900">{tenantAdmins.length} Active Admins</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl"><Cpu className="w-6 h-6" /></div>
          <div>
            <div className="text-xs text-slate-500 font-bold">AlmaLinux Server Load</div>
            <div className="text-xl font-black text-slate-900">0.12 (Optimal)</div>
          </div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex items-center gap-3">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl"><Database className="w-6 h-6" /></div>
          <div>
            <div className="text-xs text-slate-500 font-bold">MariaDB Health</div>
            <div className="text-xl font-black text-slate-900">100% Operational</div>
          </div>
        </div>
      </div>

      {/* Add Admin Modal Form */}
      {isAddAdminOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 space-y-4 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Add New Tenant Admin & Organization
              </h3>
              <button onClick={() => setIsAddAdminOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTenantAdmin} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Admin Full Name</label>
                  <input
                    type="text"
                    required
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    placeholder="e.g. Dr. Sarah Jenkins"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Admin Email</label>
                  <input
                    type="email"
                    required
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="s.jenkins@school.edu"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">School / Organization Name</label>
                  <input
                    type="text"
                    required
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    placeholder="e.g. Oakridge Rehab Center"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Subscription Plan</label>
                  <select
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  >
                    <option value="ENTERPRISE_SCHOOL">Enterprise Special School</option>
                    <option value="PRO_CLINIC">Pro Rehabilitation Clinic</option>
                    <option value="GOVT_REHAB">Government Rehab District</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-2">Initial Enabled Modules for this Admin:</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 bg-slate-50 border rounded-lg">
                  {ALL_16_MODULES.map((mod) => {
                    const checked = selectedModules.includes(mod.key);
                    return (
                      <label key={mod.key} className="flex items-center gap-2 p-1.5 bg-white border rounded cursor-pointer hover:bg-slate-100">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              setSelectedModules(selectedModules.filter(m => m !== mod.key));
                            } else {
                              setSelectedModules([...selectedModules, mod.key]);
                            }
                          }}
                          className="rounded text-blue-600"
                        />
                        <span className="text-[11px] font-semibold text-slate-800">{mod.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setIsAddAdminOpen(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow"
                >
                  Save & Provision Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tenant Admins & Feature Toggle Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
        <h3 className="text-base font-bold text-slate-900">
          Provisioned Tenant Administrators & Feature Matrix
        </h3>

        <div className="space-y-6">
          {tenantAdmins.map((tenant) => (
            <div key={tenant.id} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-200 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-bold text-slate-900">{tenant.schoolName}</h4>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-extrabold rounded uppercase">
                      {tenant.plan}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                    <span><strong>Admin:</strong> {tenant.adminName} ({tenant.adminEmail})</span>
                    <span>•</span>
                    <span><strong>Domain:</strong> {tenant.slug}.abilityos.bugfreesolution.com</span>
                    <span>•</span>
                    <span><strong>Enrolled Students:</strong> {tenant.studentCount}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-slate-500">
                    {tenant.modules.length} / 16 Modules Enabled
                  </span>
                </div>
              </div>

              {/* Module Toggle Buttons */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Real-Time Feature Enablement Toggles:
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                  {ALL_16_MODULES.map((mod) => {
                    const isEnabled = tenant.modules.includes(mod.key);
                    return (
                      <div 
                        key={mod.key}
                        onClick={() => toggleModuleForAdmin(tenant.id, mod.key)}
                        className={`p-2.5 rounded-xl border cursor-pointer transition-all flex items-center justify-between text-xs ${
                          isEnabled 
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-sm font-bold' 
                            : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-100'
                        }`}
                      >
                        <span className="truncate pr-2">{mod.label}</span>
                        <div className={`w-8 h-4 rounded-full p-0.5 transition-colors ${isEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                          <div className={`w-3 h-3 rounded-full bg-white transition-transform ${isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
